import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextInput,
  RadioButtonGroup,
  RadioButton,
  Tooltip,
  Button,
  FileUploader,
  Loading,
  Tag,
} from "@carbon/react";
import {
  Information,
  Document,
  TrashCan,
  View,
} from "@carbon/icons-react";
import CommonFields from "../common/CommonField";
import { callApi } from "../../../services/apiService";

// Constants
const S3_BASE_URL = "https://vaasalbucket.s3.ap-south-1.amazonaws.com/";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

const AdditionalInfo = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 5,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onBack,
  CommonButtons,
  onNavigateNext,
  contactData,
  request_type = "form_attributes",
}) => {
  // Refs for file inputs
  const fileInputRefs = useRef({});

 
  // -----------------------------
  // STATE
  // -----------------------------
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});

  // Form values
  const [form, setForm] = useState({
    ownership: "",
    encumbranceCertificate: "",
    conversionCertificate: "",
    reraApproved: "",
    saleDeedCertificate: "",
    leaseTerm: "",
  });

  // Document state
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [uploadedFields, setUploadedFields] = useState({});
  const [documentUploads, setDocumentUploads] = useState(() => ({
    saleDeedCertificate: { files: [] },
    encumbranceCertificate: { files: [] },
    conversionCertificate: { files: [] },
    reraApproved: { files: [] },
    additionalDocuments: { files: [] }
  }));

  // -----------------------------
  // DOCUMENT TYPE CONFIGURATION
  // -----------------------------
  const DOCUMENT_FIELDS = useMemo(() => ({
    saleDeedCertificate: {
      documentType: "Sale Deed Certificate",
      label: "Sale Deed Certificate",
      required: false,
      multiple: true,
      conditionalOn: {
        field: "saleDeedCertificate",
        condition: (value, options) => {
          const selected = options.find(opt => String(opt.id) === String(value));
          return selected?.label?.toLowerCase() === "yes";
        }
      }
    },

    encumbranceCertificate: {
      documentType: "Encumbrance Certificate",
      label: "Encumbrance Certificate",
      required: false,
      multiple: true,
      conditionalOn: {
        field: "encumbranceCertificate",
        condition: (value, options) => {
          const selected = options.find(opt => String(opt.id) === String(value));
          return selected?.label?.toLowerCase() === "yes";
        }
      }
    },
    conversionCertificate: {
      documentType: "Conversion Certificate",
      label: "Conversion Certificate",
      required: false,
      multiple: true,
      conditionalOn: {
        field: "conversionCertificate",
        condition: (value, options) => {
          const selected = options.find(opt => String(opt.id) === String(value));
          return selected?.label?.toLowerCase() === "yes";
        }
      }
    },
    reraApproved: {
      documentType: "RERA Approval",
      label: "RERA Approval",
      required: false,
      multiple: true,
      conditionalOn: {
        field: "reraApproved",
        condition: (value, options) => {
          const selected = options.find(opt => String(opt.id) === String(value));
          return selected?.label?.toLowerCase() === "yes";
        }
      }
    },
    additionalDocuments: {
      documentType: "Additional Documents",
      label: "Additional Documents",
      required: false,
      multiple: true
    }
  }), []);

  // -----------------------------
  // FIELD MAP
  // -----------------------------
  const FIELD_MAP = useMemo(() => ({
    ownership: "land_ownership",
    encumbranceCertificate: "encumbrance_certificate",
    conversionCertificate: "conversion_certificate",
    reraApproved: "rera_approved",
    saleDeedCertificate: "resale_sale_deed_certificate",
    leaseTerm: "lease_term",
  }), []);

  // -----------------------------
  // TOOLTIP MESSAGES
  // -----------------------------
  const TOOLTIP_MESSAGES = useMemo(() => ({
    ownership: "Type of property ownership rights",
    saleDeedCertificate: "Legal document proving ownership transfer from seller to buyer",
    encumbranceCertificate: "Certificate confirming property is free from legal dues and mortgages",
    conversionCertificate: "Document for land use conversion (agricultural to residential/commercial)",
    reraApproved: "Real Estate Regulatory Authority approval for the project",
    leaseTerm: "Duration of the lease agreement in years"
  }), []);

  // -----------------------------
  // UTILITY FUNCTIONS
  // -----------------------------
  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getFullDocumentUrl = useCallback((url = "") => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const normalized = url.replace(/^property\/documents\//, "documents/property/");
    return `${S3_BASE_URL}${normalized}`;
  }, []);

  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      const match = normalize(apiKey);
      return attributeValues
        .filter((a) => a.isActive && normalize(a.attrName) === match)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP, normalize]
  );

  // Get ownership options from attribute values
  const ownershipOptions = useMemo(() => {
    return getOptions("ownership") || [];
  }, [getOptions]);

  // Document type options from attribute values
  const documentTypeOptions = useMemo(() => {
    return attributeValues.filter(
      item => normalize(item.attrName) === normalize("document_type")
    );
  }, [attributeValues, normalize]);

  const getSelectedOption = useCallback((fieldKey, value) => {
    const options = getOptions(fieldKey);
    return options.find(opt => String(opt.id) === String(value));
  }, [getOptions]);

  const isYesSelected = useCallback((fieldKey) => {
    const selected = getSelectedOption(fieldKey, form[fieldKey]);
    return selected?.label?.toLowerCase() === "yes";
  }, [form, getSelectedOption]);

  const shouldShowDocumentUpload = useCallback((docFieldKey) => {
    const config = DOCUMENT_FIELDS[docFieldKey];
    if (!config.conditionalOn) return true;
    
    const { field, condition } = config.conditionalOn;
    return condition(form[field], getOptions(field));
  }, [form, getOptions, DOCUMENT_FIELDS]);

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
    
    // Clear dependent documents if field is changed from Yes to something else
    if (DOCUMENT_FIELDS[key]) {
      const wasYes = isYesSelected(key);
      const isNowYes = getSelectedOption(key, value)?.label?.toLowerCase() === "yes";
      
      if (wasYes && !isNowYes) {
        setDocumentUploads((prev) => ({
          ...prev,
          [key]: { files: [] }
        }));
        setUploadedFields((prev) => ({ ...prev, [key]: false }));
      }
    }
  }, [isYesSelected, getSelectedOption, DOCUMENT_FIELDS]);

  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const isLeaseholdSelected = useMemo(() => {
    if (!form.ownership) return false;
    
    const selectedOption = ownershipOptions.find(
      opt => String(opt.id) === String(form.ownership)
    );
    return selectedOption?.label?.toLowerCase().includes("leasehold");
  }, [form.ownership, ownershipOptions]);

  // -----------------------------
  // DATA HYDRATION
  // -----------------------------
  useEffect(() => {
    if (!data || !attributeValues.length) return;

    const additionalInfoData = data?.additional_information || {};
    const updated = { ...form };

    Object.entries(FIELD_MAP).forEach(([fieldKey, apiKey]) => {
      const value = additionalInfoData[apiKey];
      
      if (value !== undefined && value !== null && value !== "") {
        updated[fieldKey] = String(value);
      }
    });

    setForm(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, attributeValues, FIELD_MAP]);

  // -----------------------------
  // DOCUMENT API CALLS
  // -----------------------------
  const fetchExistingDocuments = useCallback(async () => {
    if (!propertyUuid) return;
    
    try {
      const response = await callApi("GET", `/property-documents/?property_uuid=${propertyUuid}`);
      
      if (Array.isArray(response.data)) {
        const docs = response.data.map(doc => ({
          id: doc.PROPERTY_DOCUMENT_ID,
          name: doc.DOCUMENT_URL ? doc.DOCUMENT_URL.split('/').pop() : "Document",
          type: doc.ATTRIBUTE_VALUE_NAME || "General",
          url: getFullDocumentUrl(doc.DOCUMENT_URL),
          date: doc.CREATED_TIMESTAMP,
          attributeId: doc.ATTRIBUTE_ID,
          attributeValueId: doc.ATTRIBUTE_VALUE_ID,
          propertyId: doc.PROPERTY_ID,
          attributeName: doc.ATTRIBUTE_NAME,
        }));
        setExistingDocuments(docs);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError("Failed to fetch existing documents");
    }
  }, [propertyUuid, getFullDocumentUrl]);

  const handleDeleteDocument = useCallback(async (documentId) => {
    if (!documentId) return;
    
    setDeletingDocId(documentId);
    try {
      await callApi("DELETE", `/property-documents/${documentId}`);
      setExistingDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setSuccess("Document deleted successfully");
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError("Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  }, []);

  const validateFile = useCallback((file) => {
    if (file.size > MAX_FILE_SIZE) {
      return `File ${file.name} exceeds 10MB limit`;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File ${file.name} type not allowed (PDF, JPEG, PNG, Word only)`;
    }
    return null;
  }, []);

  const handleFileUpload = useCallback((field, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      const error = validateFile(file);
      if (error) {
        setError(error);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setError("");
    setDocumentUploads((prev) => ({
      ...prev,
      [field]: {
        files: [...prev[field].files, ...validFiles]
      }
    }));
    setUploadedFields((prev) => ({ ...prev, [field]: false }));
    
    if (fileInputRefs.current[field]) {
      fileInputRefs.current[field].value = '';
    }
  }, [validateFile]);

  const removeFile = useCallback((field, index) => {
    setDocumentUploads((prev) => {
      const newFiles = prev[field].files.filter((_, i) => i !== index);
      return {
        ...prev,
        [field]: { files: newFiles }
      };
    });
    
    if (documentUploads[field]?.files.length === 1) {
      setUploadedFields((prev) => ({ ...prev, [field]: false }));
    }
  }, [documentUploads]);

  const viewFile = useCallback((file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    } else {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  }, []);

  const uploadDocument = useCallback(async (file, field) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('property_uuid', propertyUuid);
    
    const documentType = DOCUMENT_FIELDS[field]?.documentType || "General Document";
    const selectedType = documentTypeOptions.find(opt => 
      normalize(opt.label) === normalize(documentType)
    );
    const attributeValueId = selectedType?.id || 2;
    
    formData.append('attribute_value_id', attributeValueId.toString());
    
    try {
      const response = await callApi("POST", "/property-documents/upload", formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.success || response.status_code === 200) {
        return { success: true };
      }
      throw new Error(response.data?.message || "Upload failed");
    } catch (err) {
      throw new Error(`Failed to upload ${file.name}: ${err.message}`);
    }
  }, [propertyUuid, documentTypeOptions, normalize, DOCUMENT_FIELDS]);

  const getDocumentsByType = useCallback((documentType) => {
    return existingDocuments.filter(doc => 
      doc.type === documentType || 
      (documentType === "Additional Documents" && !Object.values(DOCUMENT_FIELDS).some(f => f.documentType === doc.type))
    );
  }, [existingDocuments, DOCUMENT_FIELDS]);

  // -----------------------------
  // VALIDATION - ALL FIELDS OPTIONAL
  // -----------------------------
  const getFieldError = useCallback(() => null, []);

  // -----------------------------
  // SAVE LOGIC
  // -----------------------------
  const handleSave = useCallback(async (navigateNext = false) => {
    setTouched(
      Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {})
    );

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      // Upload documents
      const uploadPromises = [];
      const fieldsToUpload = [];

      Object.keys(DOCUMENT_FIELDS).forEach((field) => {
        if (!shouldShowDocumentUpload(field)) return;
        
        const files = documentUploads[field]?.files || [];
        if (files.length === 0 || uploadedFields[field]) return;

        files.forEach((file) => {
          uploadPromises.push(uploadDocument(file, field));
        });
        fieldsToUpload.push(field);
      });

      if (uploadPromises.length > 0) {
        setUploading(true);
        const results = await Promise.allSettled(uploadPromises);
        
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setError(`${failed.length} upload(s) failed. Please try again.`);
        }

        fieldsToUpload.forEach((field) => {
          setUploadedFields((prev) => ({ ...prev, [field]: true }));
        });
        
        setSuccess(`Successfully uploaded ${results.filter(r => r.status === 'fulfilled').length} document(s)`);
        setUploading(false);
      }

      // Save form attributes
      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        payload[apiKey] = v;
      }

      await saveSectionAttributes(uuid, payload, form, "additional_information");

      // Clear uploaded files
      const updatedUploads = { ...documentUploads };
      fieldsToUpload.forEach((field) => {
        updatedUploads[field] = { files: [] };
      });
      setDocumentUploads(updatedUploads);

      // Refresh documents
      await fetchExistingDocuments();

      setSuccess("Additional information saved successfully!");

      onSaveSuccess?.(uuid, form, currentStep);

      if (navigateNext) {
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save additional information");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }, [
    form,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    currentStep,
    FIELD_MAP,
    saveSectionAttributes,
    onNavigateNext,
    documentUploads,
    uploadedFields,
    shouldShowDocumentUpload,
    uploadDocument,
    fetchExistingDocuments,
    DOCUMENT_FIELDS
  ]);

  // -----------------------------
  // BUTTON HANDLERS
  // -----------------------------
  const handleSaveOnly = useCallback(() => handleSave(false), [handleSave]);
  const handleSaveAndNext = useCallback(() => handleSave(true), [handleSave]);

  // -----------------------------
  // EFFECTS
  // -----------------------------
  useEffect(() => {
    if (propertyUuid) {
      fetchExistingDocuments();
    }
  }, [propertyUuid, fetchExistingDocuments]);

  // -----------------------------
  // RENDER HELPERS
  // -----------------------------
  const { renderDropdown } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  const renderDropdownWithTooltip = useCallback((fieldKey, label) => {
    const tooltipMessage = TOOLTIP_MESSAGES[fieldKey];
    
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>{label}</span>
          {tooltipMessage && (
            <Tooltip align="top" label={tooltipMessage}>
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <Information size={16} />
              </button>
            </Tooltip>
          )}
        </div>
        {renderDropdown(fieldKey, "", false)}
      </div>
    );
  }, [TOOLTIP_MESSAGES, renderDropdown]);

  const renderOwnershipRadio = useCallback(() => {
    return (
      <FormGroup legendText={
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span>Ownership Type</span>
          <Tooltip align="top" label={TOOLTIP_MESSAGES.ownership}>
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center"
              }}
            >
              <Information size={16} />
            </button>
          </Tooltip>
        </div>
      }>
        <RadioButtonGroup
          name="ownership"
          valueSelected={String(form.ownership || "")}
          onChange={(value) => updateField('ownership', value)}
        >
          {ownershipOptions.map((option) => (
            <RadioButton
              key={option.id}
              id={`ownership-${option.id}`}
              labelText={option.label}
              value={String(option.id)}
            />
          ))}
        </RadioButtonGroup>
      </FormGroup>
    );
  }, [TOOLTIP_MESSAGES, form.ownership, ownershipOptions, updateField]);

  const renderLeaseTermField = useCallback(() => {
    if (!isLeaseholdSelected) return null;

    return (
      <Grid>
        <Column sm={4} md={4} lg={8}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Lease Term</span>
            <Tooltip align="top" label={TOOLTIP_MESSAGES.leaseTerm}>
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <Information size={16} />
              </button>
            </Tooltip>
          </div>
          <TextInput
            id="leaseTerm"
            value={form.leaseTerm}
            onChange={(e) => updateField('leaseTerm', e.target.value)}
            onBlur={() => handleBlur('leaseTerm')}
            placeholder="Enter years"
            type="number"
            min="1"
            max="999"
          />
          <div style={{
            fontSize: "0.75rem",
            color: "#8a8a8a",
            marginTop: "0.25rem",
            textAlign: "right"
          }}>
            years
          </div>
        </Column>
      </Grid>
    );
  }, [isLeaseholdSelected, TOOLTIP_MESSAGES, form.leaseTerm, updateField, handleBlur]);

  const renderFileList = useCallback((files, onRemove, onView, showSize = true) => {
    return files.map((file, index) => (
      <div
        key={file.id || index}
        style={{
          padding: '0.5rem',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          marginBottom: '0.5rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Document size={16} />
          <span style={{ fontSize: '0.875rem' }}>{file.name}</span>
          {showSize && file.size && (
            <Tag type="gray" size="sm" style={{ fontSize: '0.75rem' }}>
              {(file.size / 1024).toFixed(1)} KB
            </Tag>
          )}
          {!showSize && (
            <Tag type="blue" size="sm" style={{ fontSize: '0.75rem' }}>
              Uploaded
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription="View file"
            onClick={() => onView(file)}
            renderIcon={View}
          />
          <Button
            kind="danger--ghost"
            size="sm"
            hasIconOnly
            iconDescription="Remove file"
            onClick={() => onRemove(index)}
            renderIcon={TrashCan}
          />
        </div>
      </div>
    ));
  }, []);

  const renderExistingDocuments = useCallback((field, documentType) => {
    const filteredDocs = getDocumentsByType(documentType);

    if (filteredDocs.length === 0) return null;

    return (
      <div style={{ marginTop: '1rem' }}>
        <h6 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
          Uploaded {documentType} ({filteredDocs.length})
        </h6>
        {renderFileList(
          filteredDocs.map(doc => ({ ...doc, size: null })),
          (index) => handleDeleteDocument(filteredDocs[index].id),
          (doc) => viewFile(doc),
          false
        )}
      </div>
    );
  }, [getDocumentsByType, renderFileList, handleDeleteDocument, viewFile]);

  const renderDocumentUpload = useCallback((field) => {
    if (!shouldShowDocumentUpload(field)) return null;

    const config = DOCUMENT_FIELDS[field];
    const uploadData = documentUploads[field] || { files: [] };
    const isUploaded = uploadedFields[field];

    return (
      <div style={{ 
        marginTop: '1rem', 
        padding: '1rem', 
        border: '1px solid #e0e0e0', 
        borderRadius: '4px', 
        backgroundColor: '#f8f8f8' 
      }}>
        <h6 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
          Upload {config.documentType}
        </h6>
        
        {isUploaded && (
          <div style={{ 
            padding: '0.5rem', 
            backgroundColor: '#e0f7fa', 
            borderRadius: '4px', 
            marginBottom: '1rem' 
          }}>
            <p style={{ fontSize: '0.875rem', color: '#007d79', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>✓</span>
              <span>Document has been uploaded successfully. You can upload another if needed.</span>
            </p>
          </div>
        )}
        
        <FileUploader
          ref={(el) => { fileInputRefs.current[field] = el; }}
          labelText={`Drag and drop ${config.documentType.toLowerCase()} or click to browse`}
          buttonLabel="Add file"
          buttonKind="primary"
          size="sm"
          filenameStatus="edit"
          accept={ALLOWED_FILE_EXTENSIONS}
          multiple={config.multiple}
          onChange={(e) => handleFileUpload(field, e)}
          disabled={uploading || saving}
          style={{ marginBottom: '1rem' }}
        />
        
        {uploadData.files.length > 0 && (
          <>
            <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              Files to upload:
            </p>
            {renderFileList(
              uploadData.files,
              (index) => removeFile(field, index),
              (file) => viewFile(file),
              true
            )}
          </>
        )}
        
        {renderExistingDocuments(field, config.documentType)}
      </div>
    );
  }, [
    shouldShowDocumentUpload,
    documentUploads,
    uploadedFields,
    handleFileUpload,
    removeFile,
    viewFile,
    renderExistingDocuments,
    renderFileList,
    uploading,
    saving,
    DOCUMENT_FIELDS
  ]);

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
              <Stack gap={6}>
                
                {/* Loading indicators */}
                {(uploading || deletingDocId) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Loading 
                      description={uploading ? "Uploading documents..." : "Deleting document..."} 
                      withOverlay={false} 
                    />
                  </div>
                )}

                {/* OWNERSHIP TYPE */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={16}>
                      {renderOwnershipRadio()}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* LEASE TERM (Conditional) */}
                {renderLeaseTermField()}

                {/* SALE DEED & KHATA CERTIFICATE */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdownWithTooltip("saleDeedCertificate", "Sale Deed Certificate")}
                      {renderDocumentUpload("saleDeedCertificate")}
                    </Column>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdownWithTooltip("conversionCertificate", "Conversion Certificate")}
                      {renderDocumentUpload("conversionCertificate")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ENCUMBRANCE & CONVERSION CERTIFICATE */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdownWithTooltip("encumbranceCertificate", "Encumbrance Certificate")}
                      {renderDocumentUpload("encumbranceCertificate")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* RERA APPROVED */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdownWithTooltip("reraApproved", "RERA Approved")}
                      {renderDocumentUpload("reraApproved")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ADDITIONAL DOCUMENTS */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={16}>
                      <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>
                        Additional Documents
                      </h4>
                      {renderDocumentUpload("additionalDocuments")}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* BUTTONS */}
                {CommonButtons && (
                  <CommonButtons
                    onSave={handleSaveOnly}
                    onNext={handleSaveAndNext}
                    showBack
                    showSave
                    showNext
                    isSaveLoading={saving || uploading}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default AdditionalInfo;